/**
 * Developer documentation, rendered at /docs + /docs/:slug and prerendered to static
 * HTML by scripts/dev/gen-content-pages.mjs (so non-JS crawlers and AI assistants read it).
 *
 * ACCURACY RULE: everything here is verified against the running system —
 * routes from api/v1/*.py, tool names from services/tools/, execution modes from the
 * engine, rate limits from middleware/rate_limit.py, and request/response shapes from
 * live calls. Do not document anything that isn't built.
 */

export type DocBlock =
  | { h: string }
  | { p: string }
  | { steps: string[] }
  | { code: string; lang?: string }
  | { note: string };

export interface DocPage {
  slug: string;
  category: string;
  title: string;
  /** Short card text on /docs. */
  summary: string;
  /** <title> override; see helpArticles.ts. */
  metaTitle?: string;
  /** <meta name="description">, 140-155 chars. Falls back to `summary`. */
  metaDescription?: string;
  body: DocBlock[];
}

export const API_BASE = 'https://app.agent-mesh.org/v1';

export const docsPages: DocPage[] = [
  {
    slug: 'quickstart',
    category: 'Getting started',
    title: 'Quickstart: your first agent via the API',
    summary: 'Log in, create an agent, activate it, run it — four calls.',
    metaDescription:
      'Get an AI agent running through the AgentMesh REST API in four calls — log in, create an agent, activate it, run it. Copy-paste curl examples.',
    body: [
      { p: 'Everything in AgentMesh is available over a REST API. The base URL is https://app.agent-mesh.org/v1 and every authenticated request takes a JWT bearer token.' },
      { h: '1. Get a token' },
      { code: `curl -X POST https://app.agent-mesh.org/v1/auth/login \\
  -H "Content-Type: application/json" \\
  -d '{"email":"you@example.com","password":"..."}'`, lang: 'bash' },
      { p: 'The response contains the user and a token pair. Use tokens.accessToken as the bearer token:' },
      { code: `{
  "user":   { "id": "...", "email": "you@example.com", "role": "USER", "status": "ACTIVE" },
  "tokens": { "accessToken": "eyJ...", "refreshToken": "eyJ..." }
}`, lang: 'json' },
      { h: '2. Create an agent' },
      { p: 'An agent is a system prompt plus a model config. The system prompt is the agent’s brief — who it is, what to do, and the rules.' },
      { code: `curl -X POST https://app.agent-mesh.org/v1/agents \\
  -H "Authorization: Bearer $TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Support Triage",
    "type": "CUSTOMER_SUPPORT",
    "description": "Classifies inbound support messages.",
    "config": {
      "system_prompt": "You are a support triage assistant. Classify each message as billing, technical, or general, summarise it in one line, and suggest the next action. Be concise.",
      "model_config": {
        "provider": "anthropic",
        "model": "claude-sonnet-4-20250514",
        "temperature": 0.3
      }
    }
  }'`, lang: 'bash' },
      { note: 'Valid type values: CUSTOMER_SUPPORT, DATA_PROCESSING, WORKFLOW_AUTOMATION, CONTENT_GENERATION, ANALYSIS, INTEGRATION, CUSTOM.' },
      { h: '3. Activate it' },
      { p: 'Agents are created INACTIVE and cannot run until you activate them.' },
      { code: `curl -X POST https://app.agent-mesh.org/v1/agents/$AGENT_ID/activate \\
  -H "Authorization: Bearer $TOKEN"`, lang: 'bash' },
      { h: '4. Run it' },
      { code: `curl -X POST https://app.agent-mesh.org/v1/agents/$AGENT_ID/execute \\
  -H "Authorization: Bearer $TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"inputData": {"message": "My invoice charged me twice this month."}}'`, lang: 'bash' },
      { p: 'The run is queued and executed asynchronously. Poll GET /v1/executions/{id} for the result, or watch it in the UI under Executions.' },
    ],
  },

  {
    slug: 'authentication',
    category: 'Getting started',
    title: 'Authentication',
    summary: 'Email/password login, JWT bearer tokens, refresh, and rate limits.',
    metaDescription:
      'Authenticate against the AgentMesh API with email and password, use the JWT bearer token, refresh it before it expires, and stay inside the rate limits.',
    body: [
      { p: 'AgentMesh uses built-in JWT authentication. You exchange email and password for a short-lived access token and a long-lived refresh token, then send the access token as a bearer token on every request.' },
      { h: 'Endpoints' },
      { code: `POST   /v1/auth/register          create an account
POST   /v1/auth/login             email + password -> token pair
POST   /v1/auth/refresh           refresh token -> new access token
POST   /v1/auth/logout            revoke the session
GET    /v1/auth/me                the current user
PATCH  /v1/auth/me                update your profile
POST   /v1/auth/change-password   change password`, lang: 'text' },
      { h: 'Using the token' },
      { code: `curl https://app.agent-mesh.org/v1/agents \\
  -H "Authorization: Bearer $ACCESS_TOKEN"`, lang: 'bash' },
      { p: 'Access tokens are short-lived. When one expires, exchange the refresh token at POST /v1/auth/refresh rather than logging in again.' },
      { h: 'Missing or invalid auth' },
      { code: `{"detail": "Authorization header missing"}`, lang: 'json' },
      { note: 'Auth endpoints are rate-limited to 5 requests/minute (3/minute for password reset and email verification). Passwords are hashed with bcrypt; a non-ACTIVE account is rejected at token validation.' },
    ],
  },

  {
    slug: 'agents',
    category: 'API',
    title: 'Agents API',
    metaTitle: 'Agents API — Create & Run AI Agents — AgentMesh',
    summary: 'Create, configure, version, activate and run agents.',
    metaDescription:
      'Create, configure, version, activate and run AI agents through the AgentMesh REST API — full request and response shapes, verified against the live system.',
    body: [
      { p: 'An agent is the unit of work: a system prompt, a model config, an optional tool allow-list, and optional structured JSON output. Every edit is versioned, so you can inspect history and roll back.' },
      { h: 'Endpoints' },
      { code: `POST   /v1/agents                              create
GET    /v1/agents                              list
GET    /v1/agents/{id}                         fetch one
PATCH  /v1/agents/{id}                         update (creates a new version)
DELETE /v1/agents/{id}                         delete
POST   /v1/agents/{id}/activate                status -> ACTIVE
POST   /v1/agents/{id}/deactivate              status -> INACTIVE
POST   /v1/agents/{id}/execute                 run it
GET    /v1/agents/{id}/versions                version history
GET    /v1/agents/{id}/versions/{version}      one version
POST   /v1/agents/{id}/rollback                roll back to a version
GET    /v1/agents/stats/summary                counts + run stats`, lang: 'text' },
      { h: 'Configuration' },
      { p: 'The config object holds the system prompt and model settings. Lower the temperature for classification and triage; raise it for drafting and content.' },
      { code: `{
  "system_prompt": "You are a lead-qualification assistant. Score each lead hot, warm, or cold and suggest the next step.",
  "model_config": {
    "provider": "anthropic",
    "model": "claude-sonnet-4-20250514",
    "temperature": 0.3
  },
  "max_tokens": 800
}`, lang: 'json' },
      { h: 'Running an agent' },
      { code: `POST /v1/agents/{id}/execute
{ "inputData": { "message": "Where is my order #12345?" } }`, lang: 'json' },
      { p: 'Input resolution follows one rule: if the agent has an input_fields allow-list, only those keys are used; otherwise the first message/prompt-style field is used verbatim; otherwise the whole input object is passed as JSON.' },
      { note: 'Agents start INACTIVE. An inactive agent will not run — activate it first.' },
    ],
  },

  {
    slug: 'workflows',
    category: 'API',
    title: 'Workflows API & execution modes',
    summary: 'Chain agents together. Six execution modes, structured hand-off, replay.',
    metaDescription:
      'Chain agents into workflows over the API. Six execution modes — sequential, parallel, conditional, fan-out, loop and DAG — with structured hand-off and replay.',
    body: [
      { p: 'A workflow connects agents so one step’s output becomes the next step’s input. That single rule explains every mode below.' },
      { h: 'Execution modes' },
      { code: `sequential    a chain — each step gets the previous step's output
parallel      every step gets the SAME input, run concurrently
conditional   branch on a field (equals, not_equals, greater_than,
              less_than, contains, exists, not_exists)
fan_out       run the SAME step once per item in a list, concurrently
loop          feed a step's output back into itself until a limit/condition
mixed         a dependency graph (DAG) via depends_on`, lang: 'text' },
      { note: 'Sequential workflows are available on the Free plan. Parallel, conditional, fan-out, loop and DAG require Pro — including for scheduled runs, which are skipped if the owner is not entitled.' },
      { h: 'Endpoints' },
      { code: `POST   /v1/workflows                                    create
GET    /v1/workflows                                    list
GET    /v1/workflows/{id}                               fetch one
PUT    /v1/workflows/{id}                               update (new version)
DELETE /v1/workflows/{id}                               delete
POST   /v1/workflows/{id}/activate                      status -> ACTIVE
POST   /v1/workflows/{id}/pause                         pause
POST   /v1/workflows/{id}/execute                       run it
POST   /v1/workflows/{id}/validate                      validate the graph
GET    /v1/workflows/{id}/executions                    run history
GET    /v1/workflows/{id}/executions/{execId}           one run (+ steps)
POST   /v1/workflows/{id}/executions/{execId}/replay    re-run with same input
POST   /v1/workflows/{id}/executions/{execId}/cancel    cancel
GET    /v1/workflows/{id}/export                        export as JSON
POST   /v1/workflows/import                             import
GET    /v1/workflows/{id}/costs                         token + cost breakdown`, lang: 'text' },
      { h: 'Running a workflow' },
      { p: 'The body wraps your JSON in inputData. Whatever you pass becomes the workflow’s input, and flows into the first step.' },
      { code: `curl -X POST https://app.agent-mesh.org/v1/workflows/$WORKFLOW_ID/execute \\
  -H "Authorization: Bearer $TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"inputData": {"message": "The app crashed twice when exporting a report."}}'`, lang: 'bash' },
      { p: 'Fan-out expects a list. Give it an items array and the same step runs once per item, concurrently:' },
      { code: `{"inputData": {"items": ["Love the new dashboard.", "Support was slow.", "Works as described."]}}`, lang: 'json' },
      { h: 'Structured hand-off' },
      { p: 'Plain text output passes to the next step as message. If the upstream agent has Structured JSON output enabled, its fields become top-level input for the next step — which is what makes conditional branching reliable, because the branch reads a real field instead of guessing at text.' },
      { note: 'Fan-out is capped at 100 items and loops at 25 iterations, so a single request cannot run away.' },
    ],
  },

  {
    slug: 'tools',
    category: 'Capabilities',
    title: 'Agent tools',
    summary: 'The ten actions an agent can take during a run — and the SSRF guard.',
    metaDescription:
      'The ten actions an AgentMesh agent can take during a run — HTTP, email, Slack, webhooks, Google Calendar — and the SSRF guard on every outbound call.',
    body: [
      { p: 'Tools are what make an agent act rather than just answer. You enable them per agent; the model then calls them during a run. GET /v1/tools lists everything available to you.' },
      { h: 'The ten tools' },
      { code: `http_request                 call any REST API (SSRF-guarded)
send_webhook                 POST to your outbound webhook
send_email                   send email (rate-limited; 'accepted', not 'delivered' — bounces are reported to you)
slack_message                post to Slack
discord_message              post to Discord
telegram_message             send a Telegram message
google_chat_message          post to Google Chat
notion_create_page           create a Notion page
google_calendar_list_events  read calendar events
google_calendar_create_event create a calendar event`, lang: 'text' },
      { h: 'Credentials' },
      { p: 'Connect Slack, Discord, Telegram, Notion, an outbound webhook, or Google Calendar (OAuth) under Account → Integrations. Credentials are encrypted at rest, and the model never sees the secret — the platform injects it when the tool executes.' },
      { h: 'The SSRF guard' },
      { p: 'Every outbound call goes through an SSRF guard: public IPs only (no localhost, no link-local, no private ranges) and no redirect-following. This matters because tools re-open a class of attack that a pure text model doesn’t have.' },
      { note: 'Each outbound tool has a per-user daily rate limit. Agents also carry a per-agent tool allow-list, so an agent can only use what you explicitly grant it.' },
    ],
  },

  {
    slug: 'automation',
    category: 'Capabilities',
    title: 'Automation: schedules & webhook triggers',
    metaTitle: 'Schedules & Webhook Triggers API — AgentMesh Docs',
    summary: 'Run workflows on a cron, or when an outside system POSTs to a private URL.',
    metaDescription:
      'Run workflows unattended over the API: cron schedules through Celery Beat, and inbound webhook triggers that fire a run the moment an event arrives.',
    body: [
      { p: 'A workflow doesn’t need a human to press Run. There are three triggers, and the trigger decides where the input comes from: manual (what you type), scheduled (a fixed JSON you set once), or webhook (the POST body).' },
      { h: 'Schedules (cron)' },
      { code: `POST   /v1/schedules            create a schedule
GET    /v1/schedules            list
PATCH  /v1/schedules/{id}       pause / update
DELETE /v1/schedules/{id}       delete`, lang: 'text' },
      { code: `{
  "workflowId": "...",
  "cron": "0 9 * * *",
  "inputData": { "prompt": "daily report" }
}`, lang: 'json' },
      { p: 'Cron is minute hour day month weekday, in UTC. 0 9 * * * is 09:00 UTC daily; */15 * * * * is every fifteen minutes; 0 9 * * 1-5 is 09:00 UTC on weekdays.' },
      { note: 'The workflow must be ACTIVE. If it is not, the scheduler disables the schedule rather than retrying forever.' },
      { h: 'Webhook triggers' },
      { code: `POST   /v1/triggers                  create a trigger (returns a hook URL)
GET    /v1/triggers                  list
POST   /v1/triggers/{id}/rotate      rotate the token
GET    /v1/triggers/{id}/deliveries  recent deliveries: ran, held, orphaned (with saved input)
DELETE /v1/triggers/{id}             delete`, lang: 'text' },
      { p: 'Creating a trigger gives you a private URL. Any system that can send a web request — Stripe, GitHub, Shopify, Zapier, your own code — POSTs to it and the workflow runs immediately, using their JSON body as its input.' },
      { code: `curl -X POST "https://app.agent-mesh.org/v1/hooks/<token>" \\
  -H "Content-Type: application/json" \\
  -d '{"prompt": "run it"}'
# -> 202 {"executionId": "...", "status": "queued"}`, lang: 'bash' },
      { p: 'Retries run once. Send the same Idempotency-Key header on every retry (Shopify’s X-Shopify-Webhook-Id and GitHub’s X-GitHub-Delivery work too) and a repeat returns 200 {"status": "duplicate"} with the original executionId instead of starting a second run. If the earlier run failed, timed out or was cancelled, the retry is held instead of re-run (it may already have done its action): you get 200 {"status": "held"} and a notification to check and re-run by hand. Without one of these headers every POST runs.' },
      { note: 'The hook URL is a secret — anyone holding it can trigger your workflow. Treat it like a password, and rotate it if it leaks.' },
    ],
  },

  {
    slug: 'errors-and-limits',
    category: 'Reference',
    title: 'Errors, rate limits & plans',
    summary: 'Error shape, the rate-limit table, and what Pro gates.',
    metaDescription:
      'Every error code the AgentMesh API returns, what causes it, and the rate and usage limits that apply — including the daily caps on the platform LLM key.',
    body: [
      { h: 'Error shape' },
      { p: 'Application errors return a consistent envelope:' },
      { code: `{"error": {"message": "Invalid email or password", "details": {}}}`, lang: 'json' },
      { p: 'Common statuses: 400 validation, 401 not authenticated, 403 not authorised, 404 not found, 409 conflict (e.g. email already exists), 429 rate-limited.' },
      { h: 'Rate limits' },
      { code: `auth               5/minute    login, register
auth_sensitive     3/minute    password reset, email verification
secrets           20/minute    writing BYO LLM keys / integrations
api              100/minute    general endpoints
api_heavy         30/minute    analytics, exports
websocket         10/minute    connection attempts`, lang: 'text' },
      { h: 'Free vs Pro' },
      { p: 'Free: agents, sequential workflows, community templates, live logs and run history. Pro ($29/mo): every orchestration mode (parallel, conditional, fan-out, loop, DAG), advanced analytics, and publishing your own templates.' },
      { note: 'The Pro gate applies to scheduled and webhook runs too — a non-sequential workflow on a schedule is skipped if the owner is not entitled.' },
    ],
  },
];

export function getDocPage(slug: string): DocPage | undefined {
  return docsPages.find((p) => p.slug === slug);
}
